import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { createOrder, verifyPayment, Order, OrderRequestDto, PaymentVerificationDto, FeeReceipt } from "../services/paymentService";
import { PaymentType, FeeType } from "../types/payment";

const API_URL = `${process.env.REACT_APP_API_URL}/fees-receipt`;

export interface FeeParticular {
  id?: number;
  description: string;
  amount: number;
}

export interface FeeReceiptRequestDto {
  studentAccountNo: string;
  particulars: FeeParticular[];
  paymentType: PaymentType;
  remark?: string;
}

// NEW: List API response DTO (matches backend FeeReceiptResponseDto)
export interface FeeReceiptResponseDto {
  id: number;
  feeTransactionDto: any;
  feeParticulars: FeeParticular[];
  localDateTime: string | number[];
}

export function useFeeReceipt() {
  const queryClient = useQueryClient();

  // Mutation to create order (Razorpay)
  const createOrderMutation = useMutation<Order, Error, OrderRequestDto>({
    mutationFn: createOrder,
  });

  // Mutation to verify payment (Razorpay)
  const verifyPaymentMutation = useMutation<FeeReceipt, Error, PaymentVerificationDto>({
    mutationFn: verifyPayment,
    onSuccess: () => {
      // Invalidate both receipts and accounts
      queryClient.invalidateQueries({ queryKey: ["feeReceipts"] });
      queryClient.invalidateQueries({ queryKey: ["studentAccounts"] });
    },
  });

  // Mutation to pay fees (General/Direct)
  const payFeesMutation = useMutation<FeeReceipt, Error, FeeReceiptRequestDto>({
    mutationFn: async (data: FeeReceiptRequestDto) => {
      const response = await axios.post<FeeReceipt>(`${API_URL}/pay-fee`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["feeReceipts"] });
      queryClient.invalidateQueries({ queryKey: ["studentAccounts"] });
    },
  });

  return {
    createOrderMutation,
    verifyPaymentMutation,
    payFeesMutation,
  };
}

export function useFeeReceipts(classId?: number | null, sessionId?: number | null, enabled: boolean = true) {
  return useQuery<FeeReceiptResponseDto[], Error>({
    queryKey: ["feeReceipts", classId, sessionId],
    queryFn: async () => {
      const response = await axios.get<FeeReceiptResponseDto[]>(`${API_URL}/${classId}/${sessionId}`);
      return response.data;
    },
    enabled: enabled && !!classId && !!sessionId,
    staleTime: 30000,
    retry: false,
    refetchOnWindowFocus: false,
  });
}
